import { useQuery } from '@tanstack/react-query';
import type { AgentDto, MapDto, PlayerDto, PublicConfigDto, RankTierDto } from '@vmt/shared';
import { useMemo } from 'react';
import { api } from '../lib/api';
import { qk } from '../lib/query-keys';

const ASSET_STALE = 10 * 60_000;

export function useConfig() {
  return useQuery({
    queryKey: qk.config,
    queryFn: () => api<PublicConfigDto>('/config'),
    staleTime: 60_000,
  });
}

function byId<T extends { id: string }>(items: T[] | undefined): Map<string, T> {
  return new Map((items ?? []).map((item) => [item.id, item]));
}

export function useMaps() {
  const query = useQuery({
    queryKey: qk.maps,
    queryFn: () => api<MapDto[]>('/maps'),
    staleTime: ASSET_STALE,
  });
  const lookup = useMemo(() => byId(query.data), [query.data]);
  return { ...query, lookup };
}

export function useAgents() {
  const query = useQuery({
    queryKey: qk.agents,
    queryFn: () => api<AgentDto[]>('/agents'),
    staleTime: ASSET_STALE,
  });
  const lookup = useMemo(() => byId(query.data), [query.data]);
  return { ...query, lookup };
}

export function useRankTiers() {
  const query = useQuery({
    queryKey: qk.rankTiers,
    queryFn: () => api<RankTierDto[]>('/rank-tiers'),
    staleTime: ASSET_STALE,
  });
  const lookup = useMemo(() => new Map((query.data ?? []).map((t) => [t.tier, t])), [query.data]);
  return { ...query, lookup };
}

export function usePlayers() {
  const query = useQuery({
    queryKey: qk.players,
    queryFn: () => api<PlayerDto[]>('/players'),
    staleTime: 30_000,
  });
  const lookup = useMemo(() => byId(query.data), [query.data]);
  return { ...query, lookup };
}
